import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const fmtMonth = (d) =>
  d.toLocaleDateString("en-US", { month: "long", year: "numeric" });

const toISO = (d) => d.toISOString().slice(0, 10);

const buildMonthGrid = (month) => {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const totalDays = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const todayStart = new Date(new Date().setHours(0, 0, 0, 0));
  const cells = Array(first.getDay()).fill(null);
  for (let i = 1; i <= totalDays; i++) {
    const d = new Date(month.getFullYear(), month.getMonth(), i);
    cells.push({ day: i, iso: toISO(d), past: d < todayStart });
  }
  return cells;
};

export const useGuestyListings = (filter) => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchListings = async () => {
      try {
        const res = await fetch(`${API}/api/guesty/listings`);
        if (!res.ok) throw new Error("listings fetch failed");
        const data = await res.json();
        const all = data.listings || [];
        const matched = filter
          ? all.filter((l) =>
              `${l.title || ""} ${l.nickname || ""} ${l.property_type || ""}`
                .toLowerCase()
                .includes(filter.toLowerCase())
            )
          : all;
        if (!matched.length) throw new Error("no listings matched");
        setListings(matched);
      } catch {
        setError("Online booking is temporarily unavailable. Please contact us to book.");
      } finally {
        setLoading(false);
      }
    };
    fetchListings();
  }, [filter]);

  return { listings, loading, error };
};

const useGuestyCalendar = (listing, month) => {
  const [days, setDays] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!listing) return;
    const start = new Date(month.getFullYear(), month.getMonth(), 1);
    const end = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    const fetchCalendar = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API}/api/guesty/listings/${listing.id}/calendar?start_date=${toISO(start)}&end_date=${toISO(end)}`
        );
        if (!res.ok) throw new Error("calendar fetch failed");
        const data = await res.json();
        const map = {};
        (data.days || []).forEach((d) => {
          map[d.date] = d;
        });
        setDays(map);
        setError(null);
      } catch {
        setError("Could not load availability for this month.");
      } finally {
        setLoading(false);
      }
    };
    fetchCalendar();
  }, [listing, month]);

  return { days, loading, error };
};

const selectionRole = (iso, selection) => {
  if (!selection?.checkIn) return null;
  if (iso === selection.checkIn || iso === selection.checkOut) return "edge";
  if (selection.checkOut && iso > selection.checkIn && iso < selection.checkOut) return "range";
  return null;
};

const dayCellClass = (past, available, role, clickable) => {
  if (past) return "border-transparent text-muted-foreground/30";
  if (role === "edge") return "border-primary bg-primary text-white";
  if (role === "range") return "border-primary/30 bg-primary/15";
  const base = available
    ? "border-primary/20 bg-primary/5 hover:bg-primary/10"
    : "border-transparent bg-muted/60 text-muted-foreground/50";
  return clickable ? `${base} cursor-pointer` : base;
};

const dayNumberClass = (past, available, role) => {
  if (role === "edge") return "text-white font-semibold";
  if (available) return "text-foreground font-medium";
  if (!past) return "line-through";
  return "";
};

const DayCell = ({ cell, info, selection, onDayClick }) => {
  const available = Boolean(info?.available) && !cell.past;
  const role = selectionRole(cell.iso, selection);
  const clickable = Boolean(onDayClick) && !cell.past;
  const handleClick = () => {
    if (clickable) onDayClick(cell.iso, info);
  };
  return (
    <div
      data-testid={`calendar-day-${cell.iso}`}
      onClick={handleClick}
      role={clickable ? "button" : undefined}
      className={`rounded-md p-1.5 md:p-2 text-center min-h-[52px] md:min-h-[60px] flex flex-col items-center justify-center border transition-colors ${dayCellClass(cell.past, available, role, clickable)} ${clickable ? "cursor-pointer" : ""}`}
    >
      <span className={`font-body text-xs md:text-sm ${dayNumberClass(cell.past, available, role)}`}>
        {cell.day}
      </span>
      {available && info?.price != null && (
        <span className={`font-body text-[10px] md:text-[11px] font-semibold mt-0.5 ${role === "edge" ? "text-white/90" : "text-primary"}`}>
          ${Math.round(info.price)}
        </span>
      )}
    </div>
  );
};

const Legend = ({ selectable }) => (
  <div className="flex flex-wrap items-center gap-5 mt-5">
    <div className="flex items-center gap-2">
      <span className="inline-block w-3 h-3 rounded-sm bg-primary/10 border border-primary/30" />
      <span className="font-body text-xs text-muted-foreground">Available (nightly rate)</span>
    </div>
    <div className="flex items-center gap-2">
      <span className="inline-block w-3 h-3 rounded-sm bg-muted border border-border" />
      <span className="font-body text-xs text-muted-foreground">Booked / Unavailable</span>
    </div>
    {selectable && (
      <div className="flex items-center gap-2">
        <span className="inline-block w-3 h-3 rounded-sm bg-primary" />
        <span className="font-body text-xs text-muted-foreground">Your selection</span>
      </div>
    )}
  </div>
);

const CalendarGrid = ({ grid, days, selection, onDayClick }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
    <div className="grid grid-cols-7 gap-1 mb-2">
      {WEEKDAYS.map((d) => (
        <div key={d} className="text-center font-body text-[11px] uppercase tracking-wider text-muted-foreground py-1">
          {d}
        </div>
      ))}
    </div>
    <div className="grid grid-cols-7 gap-1">
      {grid.map((cell, i) =>
        cell === null ? (
          <div key={`empty-${i}`} />
        ) : (
          <DayCell key={cell.iso} cell={cell} info={days[cell.iso]} selection={selection} onDayClick={onDayClick} />
        )
      )}
    </div>
    <Legend selectable={Boolean(onDayClick)} />
  </motion.div>
);

export const LiveAvailability = ({ listing, selection = null, onDayClick = null }) => {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const { days, loading, error } = useGuestyCalendar(listing, month);
  const grid = useMemo(() => buildMonthGrid(month), [month]);

  const today = new Date();
  const minMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const maxMonth = new Date(today.getFullYear(), today.getMonth() + 12, 1);

  if (!listing) return null;

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-16" data-testid="availability-loading">
          <Loader2 className="animate-spin text-primary" size={28} />
        </div>
      );
    }
    if (error) {
      return (
        <p data-testid="availability-error" className="font-body text-sm text-muted-foreground py-8 text-center">
          {error}
        </p>
      );
    }
    return <CalendarGrid grid={grid} days={days} selection={selection} onDayClick={onDayClick} />;
  };

  return (
    <div data-testid="live-availability-calendar" className="border border-border/60 rounded-xl p-5 md:p-7 bg-white">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground">
            {fmtMonth(month)}
          </h3>
          <p className="font-body text-xs text-muted-foreground mt-1">
            Live rates & availability — {listing.title || listing.nickname}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="calendar-prev-month"
            variant="outline"
            size="icon"
            className="rounded-full h-9 w-9"
            disabled={month <= minMonth}
            onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
          >
            <ChevronLeft size={16} />
          </Button>
          <Button
            data-testid="calendar-next-month"
            variant="outline"
            size="icon"
            className="rounded-full h-9 w-9"
            disabled={month >= maxMonth}
            onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>
      {renderBody()}
    </div>
  );
};
